import React, { useState, useEffect } from 'react';
import { Button, Navbar, Container } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  HomeIcon, 
  ShoppingBagIcon, 
  ShoppingCartIcon, 
  ClipboardDocumentListIcon,
  ChartBarIcon,
  BuildingStorefrontIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ClockIcon,
  LifebuoyIcon,
  Cog6ToothIcon,
  EnvelopeIcon,
  EyeIcon,
  ShieldExclamationIcon,
  QuestionMarkCircleIcon,
  BookOpenIcon,
} from '@heroicons/react/24/solid';
import { useAuth } from '../../contexts/AuthContext';
import { useSiteConfig } from '../../contexts/SiteConfigContext';
import { activityLogAPI } from '../../services/activityTracker';
import { siteConfigAPI } from '../../services/api';
import LogoDisplay from './LogoDisplay';

interface BannerData {
  id: number;
  message: string;
  trigger_type: 'always' | 'days_before_expiration' | 'after_expiration' | 'first_login';
  trigger_days: number;
  background_color: string;
  text_color: string;
  is_dismissible: boolean;
  button_text?: string;
  button_url?: string;
  button_bg_color?: string;
  button_text_color?: string;
}

function filterBanners(
  banners: BannerData[],
  daysRemaining?: number | null,
  isExpired?: boolean,
): BannerData[] {
  return banners.filter(b => {
    if (b.trigger_type === 'always') return true;
    if (b.trigger_type === 'first_login') return true;
    if (b.trigger_type === 'days_before_expiration') {
      if (daysRemaining != null) {
        return daysRemaining <= b.trigger_days && !isExpired;
      }
      // No expiration date set: show the banner anyway
      return true;
    }
    if (b.trigger_type === 'after_expiration') {
      return !!isExpired;
    }
    return false;
  });
}

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { getSiteName } = useSiteConfig();
  const [lastLoginTime, setLastLoginTime] = useState<string | null>(null);
  const [allBanners, setAllBanners] = useState<BannerData[]>([]);

  const banners = filterBanners(allBanners, user?.days_remaining, user?.is_expired);

  useEffect(() => {
    siteConfigAPI.getBanners()
      .then(res => setAllBanners(res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user && user.role === 'customer') {
      activityLogAPI.getMyLastLogin()
        .then(res => {
          if (res.data.timestamp) {
            setLastLoginTime(res.data.timestamp);
          }
        })
        .catch(() => {});
    }
  }, [user]);

  const formatLastLogin = (dateStr?: string | null) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + 
      ' at ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/customer-login');
  };

  const isActive = (path: string) => location.pathname === path;

  const desktopButtonStyle = {
    borderRadius: '999px',
    padding: '8px 20px',
    borderWidth: '2px',
    fontWeight: '500' as const,
    fontSize: '0.9rem'
  };

  const isAdmin = user?.role === 'admin';

  const mobileNavItems = isAdmin
    ? [
        { path: '/manage/settings', icon: Cog6ToothIcon, label: 'Settings' },
        { path: '/manage/email-templates', icon: EnvelopeIcon, label: 'Emails' },
        { path: '/manage/help-center', icon: QuestionMarkCircleIcon, label: 'Help' },
        { path: '/manage/user-guide', icon: BookOpenIcon, label: 'Guide' },
        { path: '/manage/activity-logs', icon: EyeIcon, label: 'Logs' },
        { path: '/manage/security-events', icon: ShieldExclamationIcon, label: 'Security' },
        { path: '/manage/support', icon: LifebuoyIcon, label: 'Support' },
      ]
    : [
        { path: '/customer-dashboard', icon: HomeIcon, label: 'Home' },
        { path: '/products', icon: ShoppingBagIcon, label: 'Shop' },
        { path: '/cart', icon: ShoppingCartIcon, label: 'Cart' },
        { path: '/orders', icon: ClipboardDocumentListIcon, label: 'Orders' },
        { path: '/settings', icon: Cog6ToothIcon, label: 'Settings' },
      ];

  return (
    <>
      {/* Top Header Bar */}
      <Navbar className="navbar-dark" style={{ backgroundColor: '#1e3a8a', padding: '0.75rem 0' }}>
        <Container fluid>
          <Navbar.Brand 
            className="d-flex align-items-center gap-2" 
            onClick={() => navigate(isAdmin ? '/manage/settings' : '/customer-dashboard')}
            style={{ cursor: 'pointer', overflow: 'hidden', minWidth: 0, flex: '1 1 0' }}
          >
            <LogoDisplay 
              type="header" 
              style={{ maxWidth: '36px', maxHeight: '36px', flexShrink: 0 }}
              fallbackIcon={<BuildingStorefrontIcon style={{ width: '24px', height: '24px', color: 'white' }} />}
              alt={`${getSiteName()} Logo`}
            />
            <span className="header-brand-text" style={{
              color: 'white', fontSize: '1.1rem', fontWeight: '600',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              minWidth: 0,
            }}>{getSiteName()}</span>
          </Navbar.Brand>

          {/* Mobile: just show logout button in header */}
          <div className="d-lg-none">
            <button
              onClick={handleLogout}
              style={{
                background: 'transparent',
                border: '1.5px solid rgba(255,255,255,0.4)',
                borderRadius: '8px',
                color: 'white',
                padding: '6px 12px',
                fontSize: '0.8rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <ArrowRightOnRectangleIcon style={{ width: '16px', height: '16px' }} />
              Logout
            </button>
          </div>

          {/* Desktop Menu Items */}
          <div className="d-none d-lg-flex align-items-center gap-2">
            {isAdmin ? (
              <>
                <Button
                  variant="outline-light"
                  className="d-flex align-items-center gap-2"
                  style={desktopButtonStyle}
                  onClick={() => navigate('/manage/settings')}
                >
                  <Cog6ToothIcon style={{ width: '16px', height: '16px' }} />
                  <span className="d-none d-xl-inline">Settings</span>
                </Button>

                <Button
                  variant="outline-light"
                  className="d-flex align-items-center gap-2"
                  style={desktopButtonStyle}
                  onClick={() => navigate('/manage/email-templates')}
                >
                  <EnvelopeIcon style={{ width: '16px', height: '16px' }} />
                  <span className="d-none d-xl-inline">Emails</span>
                </Button>

                <Button
                  variant="outline-light"
                  className="d-flex align-items-center gap-2"
                  style={desktopButtonStyle}
                  onClick={() => navigate('/manage/help-center')}
                >
                  <QuestionMarkCircleIcon style={{ width: '16px', height: '16px' }} />
                  <span className="d-none d-xl-inline">Help</span>
                </Button>

                <Button
                  variant="outline-light"
                  className="d-flex align-items-center gap-2"
                  style={desktopButtonStyle}
                  onClick={() => navigate('/manage/user-guide')}
                >
                  <BookOpenIcon style={{ width: '16px', height: '16px' }} />
                  <span className="d-none d-xl-inline">Guide</span>
                </Button>

                <Button
                  variant="outline-light"
                  className="d-flex align-items-center gap-2"
                  style={desktopButtonStyle}
                  onClick={() => navigate('/manage/activity-logs')}
                >
                  <EyeIcon style={{ width: '16px', height: '16px' }} />
                  <span className="d-none d-xl-inline">Activity Logs</span>
                </Button>

                <Button
                  variant="outline-light"
                  className="d-flex align-items-center gap-2"
                  style={desktopButtonStyle}
                  onClick={() => navigate('/manage/security-events')}
                >
                  <ShieldExclamationIcon style={{ width: '16px', height: '16px' }} />
                  <span className="d-none d-xl-inline">Security</span>
                </Button>

                <Button
                  variant="outline-light"
                  className="d-flex align-items-center gap-2"
                  style={desktopButtonStyle}
                  onClick={() => navigate('/manage/support')}
                >
                  <LifebuoyIcon style={{ width: '16px', height: '16px' }} />
                  <span className="d-none d-xl-inline">Support</span>
                </Button>
              </>
            ) : (
              <>
            <Button 
              variant="outline-light"
              className="d-flex align-items-center gap-2"
              style={desktopButtonStyle}
              onClick={() => navigate('/customer-dashboard')}
            >
              <HomeIcon style={{ width: '16px', height: '16px' }} />
              <span className="d-none d-xl-inline">Dashboard</span>
            </Button>
            
            <Button 
              variant="outline-light"
              className="d-flex align-items-center gap-2"
              style={desktopButtonStyle}
              onClick={() => navigate('/products')}
            >
              <ShoppingBagIcon style={{ width: '16px', height: '16px' }} />
              <span className="d-none d-xl-inline">Products</span>
            </Button>
            
            <Button 
              variant="outline-light"
              className="d-flex align-items-center gap-2"
              style={desktopButtonStyle}
              onClick={() => navigate('/cart')}
            >
              <ShoppingCartIcon style={{ width: '16px', height: '16px' }} />
              <span className="d-none d-xl-inline">Cart</span>
            </Button>
            
            <Button 
              variant="outline-light"
              className="d-flex align-items-center gap-2"
              style={desktopButtonStyle}
              onClick={() => navigate('/orders')}
            >
              <ClipboardDocumentListIcon style={{ width: '16px', height: '16px' }} />
              <span className="d-none d-xl-inline">Orders</span>
            </Button>
            
            <Button 
              variant="outline-light"
              className="d-flex align-items-center gap-2"
              style={desktopButtonStyle}
              onClick={() => navigate('/reports')}
            >
              <ChartBarIcon style={{ width: '16px', height: '16px' }} />
              <span className="d-none d-xl-inline">Reports</span>
            </Button>
            
            <Button 
              variant="outline-light"
              className="d-flex align-items-center gap-2"
              style={desktopButtonStyle}
              onClick={() => navigate('/support')}
            >
              <LifebuoyIcon style={{ width: '16px', height: '16px' }} />
              <span className="d-none d-xl-inline">Support</span>
            </Button>

            <Button 
              variant="outline-light"
              className="d-flex align-items-center gap-2"
              style={desktopButtonStyle}
              onClick={() => navigate('/settings')}
            >
              <Cog6ToothIcon style={{ width: '16px', height: '16px' }} />
              <span className="d-none d-xl-inline">Settings</span>
            </Button>
              </>
            )}
            
            <Button 
              variant="outline-light"
              className="d-flex align-items-center gap-2"
              style={desktopButtonStyle}
              onClick={handleLogout}
            >
              <span className="d-none d-xl-inline">Logout</span>
              <span className="d-xl-none">Exit</span>
            </Button>
          </div>
        </Container>
      </Navbar>

      {/* Customer Info Bar */}
      {user && user.role === 'customer' && (
        <div style={{
          backgroundColor: '#1e40af',
          padding: '6px 0',
          fontSize: '0.8rem',
          color: 'rgba(255,255,255,0.85)',
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Container fluid className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <UserCircleIcon style={{ width: '16px', height: '16px', color: 'rgba(255,255,255,0.7)' }} />
              <span style={{ fontWeight: '500' }}>{user.company_name || user.username}</span>
              {user.customer_id && (
                <span style={{ 
                  backgroundColor: 'rgba(255,255,255,0.15)', 
                  padding: '1px 8px', 
                  borderRadius: '10px',
                  fontSize: '0.75rem',
                  letterSpacing: '0.5px'
                }}>
                  {user.customer_id}
                </span>
              )}
            </div>
            <div className="d-none d-sm-flex align-items-center gap-2">
              <ClockIcon style={{ width: '14px', height: '14px', color: 'rgba(255,255,255,0.6)' }} />
              <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>
                Last login: {formatLastLogin(lastLoginTime || user.last_login)}
              </span>
            </div>
          </Container>
        </div>
      )}

      {/* Admin-configured Notification Banners */}
      {banners.map((banner) => (
        <div key={banner.id} style={{
          background: banner.background_color,
          padding: '9px 0',
          fontSize: '0.84rem',
          color: banner.text_color,
          textAlign: 'center',
          fontWeight: '600',
        }}>
          <Container fluid className="d-flex justify-content-center align-items-center gap-2 flex-wrap">
            <span>{banner.message}</span>
            {banner.button_text && (
              <button
                type="button"
                onClick={() => { if (banner.button_url) { /^https?:\/\//.test(banner.button_url) ? window.open(banner.button_url, '_blank', 'noopener') : navigate(banner.button_url); } }}
                style={{
                  backgroundColor: banner.button_bg_color || 'rgba(255,255,255,0.25)',
                  color: banner.button_text_color || banner.text_color,
                  padding: '2px 12px',
                  borderRadius: '10px',
                  fontSize: '0.75rem',
                  cursor: banner.button_url ? 'pointer' : 'default',
                  marginLeft: '4px',
                  border: 'none',
                  lineHeight: 'inherit',
                  fontWeight: 'inherit',
                }}
              >
                {banner.button_text}
              </button>
            )}
            {(banner.is_dismissible || banner.trigger_type === 'first_login') && (
              <button
                type="button"
                onClick={() => {
                  siteConfigAPI.dismissBanner(banner.id).catch(() => {});
                  setAllBanners(prev => prev.filter(b => b.id !== banner.id));
                }}
                style={{
                  cursor: 'pointer',
                  marginLeft: '8px',
                  opacity: 0.7,
                  fontSize: '1rem',
                  lineHeight: 1,
                  background: 'none',
                  border: 'none',
                  color: 'inherit',
                  padding: 0,
                }}
                title="Dismiss"
                aria-label="Dismiss banner"
              >
                ✕
              </button>
            )}
          </Container>
        </div>
      ))}

      {/* Mobile Bottom Navigation Bar */}
      <nav className="mobile-bottom-nav d-lg-none">
        {mobileNavItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`bottom-nav-item ${active ? 'active' : ''}`}
            >
              <Icon style={{ width: '20px', height: '20px' }} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
};

export default Header;